import { isUserGeneratedKey } from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { z } from "zod";

export type ProgramType = {
  id?: string;
  label: string;
  key?: string;
  order: number;
};

export type ProgramGroupType = {
  id?: string;
  organization_id: string;
  name: string;
  programs: ProgramType[];
  programs_to_delete_ids?: string[];
};

export type ProgramFormInputType = {
  id?: string;
  label: string;
  key: string;
  order: string;
  userGeneratedKey: boolean;
};

const ProgramSchema = z.object({
  id: z.string().optional(),
  label: z.string().min(1, "This field is required"),
  key: z.string().min(1, "This field is required"),
  order: z.number().int(),
});

export const ProgramGroupSchema = z.object({
  id: z.string().optional(),
  organization_id: z.string().min(1, "This field is required"),
  name: z.string().min(1, "This field is required"),
  programs: z.array(ProgramSchema).min(1),
  programs_to_delete_ids: z.array(z.string()).optional(),
});

const ProgramFormSchema = z.object({
  id: z.string().optional(),
  label: z.string().min(1, "This field is required"),
  key: z.string().min(1, "This field is required"),
  order: z
    .string()
    .min(1, "This field is required")
    .refine((value) => !Number.isNaN(parseInt(value, 10)), {
      message: "Must be a number",
    }),
  userGeneratedKey: z.boolean().default(false),
});

export const ProgramGroupFormSchema = z.object({
  organizationId: z.string().min(1, "This field is required"),
  name: z.string().min(1, "This field is required"),
  programs: z.array(ProgramFormSchema).min(1),
  programsToDeleteIds: z.array(z.string()),
});

export function programFormInputToProgramType(
  programFormInput: ProgramFormInputType
) {
  const program = {
    label: programFormInput.label,
    key: programFormInput.key,
    order: programFormInput.order ? Number(programFormInput.order) : Number.NaN,
  };
  return programFormInput.id
    ? { id: programFormInput.id, ...program }
    : program;
}

export function programToProgramFormInputType(
  program: ProgramType
): ProgramFormInputType {
  return {
    id: program.id,
    label: program.label,
    key: program.key ?? "",
    order: program.order.toString(),
    userGeneratedKey: isUserGeneratedKey(program.key, program.label),
  };
}

export type ProgramFormType = z.infer<typeof ProgramFormSchema>;
export type ProgramGroupFormType = z.infer<typeof ProgramGroupFormSchema>;
